import { useState } from "react";
import { Check } from "lucide-react";
import { Reveal } from "@/components/pause/motion";
import { api, formatApiError } from "@/lib/api";

export const Waitlist = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [comment, setComment] = useState("");
  const [interest, setInterest] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");

  const interests = ["1-on-1 Sessions", "Weekly Practice", "Retreat Escapes"];

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading" || status === "done") return;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!name.trim()) {
      setStatus("error");
      setMessage("Please add your name.");
      return;
    }
    if (!emailOk) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await api.post("/waitlist", {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        location: location.trim() || null,
        interest: interest || null,
        message: comment.trim() || null,
      });
      setStatus("done");
      setMessage(
        data.status === "already_subscribed" ? "You're already on the list — see you soon." : "You're on the list!"
      );
    } catch (err) {
      setStatus("error");
      setMessage(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="bg-[#F5C800] text-[#1A1A1A] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col min-[900px]:flex-row justify-between gap-14 min-[900px]:gap-20">
        {/* Left */}
        <div className="min-[900px]:w-1/2">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.28em] text-[#1A1A1A]/60 mb-6">Join the List</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-light text-6xl md:text-8xl leading-[0.9] tracking-tight">
              Take a <br />
              <span className="italic">breath</span> with us.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-md font-body text-base md:text-lg text-[#1A1A1A]/70 leading-relaxed">
              New classes, retreat dates, and the occasional note on slowing down. No noise — just the good stuff.
            </p>
          </Reveal>
        </div>

        {/* Right — form */}
        <div className="min-[900px]:w-1/2 min-[900px]:max-w-md w-full min-[900px]:pt-4">
          <Reveal delay={0.1}>
            <form onSubmit={submit} noValidate data-testid="waitlist-form" className="space-y-9">
              <div>
                <label htmlFor="wl-name" className="font-body text-xs uppercase tracking-[0.16em] text-[#1A1A1A]/60">
                  Your name
                </label>
                <input
                  id="wl-name"
                  data-testid="waitlist-name-input"
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={status === "done"}
                  placeholder="Jane Doe"
                  className="mt-2 w-full bg-transparent border-b-2 border-[#1A1A1A] py-2 font-body text-lg placeholder-[#1A1A1A]/35 focus:outline-none focus:border-[#3B5A3A] transition-colors duration-300"
                />
              </div>

              <div>
                <label htmlFor="wl-email" className="font-body text-xs uppercase tracking-[0.16em] text-[#1A1A1A]/60">
                  Email
                </label>
                <input
                  id="wl-email"
                  data-testid="waitlist-email-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "done"}
                  placeholder="you@email.com"
                  className="mt-2 w-full bg-transparent border-b-2 border-[#1A1A1A] py-2 font-body text-lg placeholder-[#1A1A1A]/35 focus:outline-none focus:border-[#3B5A3A] transition-colors duration-300"
                />
              </div>

              <div>
                <label htmlFor="wl-phone" className="font-body text-xs uppercase tracking-[0.16em] text-[#1A1A1A]/60">
                  Phone <span className="text-[#1A1A1A]/40 lowercase tracking-normal">(optional)</span>
                </label>
                <input
                  id="wl-phone"
                  data-testid="waitlist-phone-input"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={status === "done"}
                  placeholder="+91 98xxxxxxxx"
                  className="mt-2 w-full bg-transparent border-b-2 border-[#1A1A1A] py-2 font-body text-lg placeholder-[#1A1A1A]/35 focus:outline-none focus:border-[#3B5A3A] transition-colors duration-300"
                />
              </div>

              <div>
                <label htmlFor="wl-location" className="font-body text-xs uppercase tracking-[0.16em] text-[#1A1A1A]/60">
                  City / Location <span className="text-[#1A1A1A]/40 lowercase tracking-normal">(optional)</span>
                </label>
                <input
                  id="wl-location"
                  data-testid="waitlist-location-input"
                  type="text"
                  name="location"
                  autoComplete="address-level2"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={status === "done"}
                  placeholder="Bengaluru, India"
                  className="mt-2 w-full bg-transparent border-b-2 border-[#1A1A1A] py-2 font-body text-lg placeholder-[#1A1A1A]/35 focus:outline-none focus:border-[#3B5A3A] transition-colors duration-300"
                />
              </div>

              <div>
                <span className="font-body text-xs uppercase tracking-[0.16em] text-[#1A1A1A]/60">
                  I&apos;m interested in (optional)
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {interests.map((it) => (
                    <button
                      type="button"
                      key={it}
                      data-testid={`waitlist-interest-${it.split(" ")[0].toLowerCase()}`}
                      onClick={() => setInterest(interest === it ? "" : it)}
                      disabled={status === "done"}
                      className={`font-body text-[13px] px-4 py-2 rounded-full border transition-colors duration-300 ${
                        interest === it
                          ? "bg-[#1A1A1A] text-[#F5C800] border-[#1A1A1A]"
                          : "border-[#1A1A1A]/40 text-[#1A1A1A]/80 hover:border-[#1A1A1A]"
                      }`}
                    >
                      {it}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="wl-comment" className="font-body text-xs uppercase tracking-[0.16em] text-[#1A1A1A]/60">
                  A question or note <span className="text-[#1A1A1A]/40 lowercase tracking-normal">(optional)</span>
                </label>
                <textarea
                  id="wl-comment"
                  data-testid="waitlist-comment-input"
                  name="message"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={status === "done"}
                  placeholder="Anything you'd like Nisha to know…"
                  className="mt-2 w-full bg-transparent border-b-2 border-[#1A1A1A] py-2 font-body text-base leading-relaxed placeholder-[#1A1A1A]/35 focus:outline-none focus:border-[#3B5A3A] transition-colors duration-300 resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="waitlist-submit"
                disabled={status === "loading" || status === "done"}
                className={`w-full font-body text-sm uppercase tracking-[0.14em] font-medium py-4 rounded-full transition-all duration-400 flex items-center justify-center gap-2 ${
                  status === "done"
                    ? "bg-[#3B5A3A] text-[#FAF7F2] cursor-default"
                    : "bg-[#1A1A1A] text-[#FAF7F2] hover:scale-[1.02]"
                }`}
              >
                {status === "done" ? (
                  <>
                    <Check className="h-4 w-4" /> You&apos;re on the list!
                  </>
                ) : status === "loading" ? (
                  "Adding you…"
                ) : (
                  "Join the List"
                )}
              </button>

              {message && (
                <p
                  data-testid="waitlist-message"
                  className={`font-body text-sm ${status === "done" ? "text-[#1A1A1A]/70" : "text-[#3B5A3A]"}`}
                >
                  {message}
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
